import { Card } from "@/components/ui/card";
import { convertBase64 } from "@/utils/utils";
import {
  ExpenseAutofillInfo,
  useParseExpenseReceiptMutation,
} from "@common/utils/graphql";
import { Body, Spinner } from "@hypercard/web-components";
import { ChangeEvent } from "react";
import { Image } from "./Image";
import { X } from "@phosphor-icons/react";
import { useToast } from "@/components/ui/use-toast";
import { Partial } from "@react-spring/web";
import { ExpenseFormValues } from "@domains/reports/pages/forms/Forms";
import { addTimezoneOffsetToUTCZeroTimeIfNeeded } from "./utils";
import { ApolloClient, useApolloClient } from "@apollo/client";
import { sanatizeOCRCustomFieldsData } from "./CustomFields";

// CONSTANTS
const IMAGE_TYPES = "image/png,image/jpeg";

const sanitizeOCRData = (
  client: ApolloClient<object>,
  data: ExpenseAutofillInfo,
  receipt: File,
) => {
  return {
    merchant: data.merchantName || "",
    amount: data.amount || undefined,
    date: data.transactionDate
      ? addTimezoneOffsetToUTCZeroTimeIfNeeded(data.transactionDate)
      : undefined,
    expenseCategoryGLId: data.expenseCategoryGLId
      ? String(data.expenseCategoryGLId)
      : undefined,
    receipt,
    customFieldEntries: sanatizeOCRCustomFieldsData(client, data.customFields),
  };
};

// TYPES
type Props = {
  onParseReceipt?: (rawData: Partial<ExpenseFormValues>) => void;
  field: any;
};

// COMPONENT
export const ReceiptUploadDropzone = ({ onParseReceipt, field }: Props) => {
  const client = useApolloClient();
  const [parseExpenseReceipt, { loading }] = useParseExpenseReceiptMutation();
  const { toast } = useToast();

  const resetFile = () => {
    field.onChange({ target: { files: null, value: null } });
  };

  if (loading) {
    return (
      <Card className="flex h-[190px] flex-col items-center justify-center space-y-4 bg-transparent">
        <Spinner className="h-4 w-4" />
        <Body size="md" weight="medium">
          Analyzing receipt information...
        </Body>
      </Card>
    );
  }

  if (field.value) {
    const imageUrl =
      typeof field.value === "string"
        ? field.value
        : URL.createObjectURL(field.value);

    return (
      <div className="relative h-[190px] w-full overflow-hidden rounded">
        <img src={imageUrl} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <button onClick={resetFile} type="button">
          <X className="absolute right-2 top-2 h-4 w-4 text-white" />
        </button>
      </div>
    );
  }

  const handleFileChange = async (files: FileList | null) => {
    // Check that a file was uploaded
    const file = files?.[0];
    if (!file) {
      toast({
        title: "No file was uploaded. Please try again.",
        variant: "destructive",
      });
      return;
    }

    // Check that the file is a supported image type
    if (!IMAGE_TYPES.includes(file.type)) {
      toast({
        title: "Invalid file type. Please upload an image file.",
        variant: "destructive",
      });
      return;
    }

    resetFile();

    // If onParseReceipt is provided, we parse
    if (onParseReceipt) {
      try {
        // Convert base64
        const base64 = await convertBase64(file);
        if (!base64) {
          throw new Error("Failed to convert uploaded image to base64");
        }

        // Send to backend and closure
        const { data } = await parseExpenseReceipt({
          variables: {
            receiptFileData: base64,
          },
        });
        if (
          data?.parseReceiptWithOCR.__typename ===
          "MutationParseReceiptWithOCRSuccess"
        ) {
          const rawData = data.parseReceiptWithOCR.data;
          onParseReceipt(sanitizeOCRData(client, rawData, file));
          field.onChange(file);
        }
      } catch (e) {
        toast({
          title:
            "The file could not be uploaded. Please try again or upload a different file.",
          variant: "destructive",
        });
      }
    } else {
      field.onChange(file);
    }
  };

  const handleOnFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    handleFileChange(e.target.files);
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const { files } = e.dataTransfer;
    handleFileChange(files);
  };

  return (
    <Card
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className="uploader-container relative flex h-[190px] border-0 bg-transparent"
    >
      <input
        id="receipt-upload-dropzone"
        type="file"
        accept={IMAGE_TYPES}
        className="absolute bottom-0 left-0 right-0 top-0 hidden"
        {...field}
        onChange={handleOnFileSelect}
      />
      <label
        htmlFor="receipt-upload-dropzone"
        className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center px-10 text-center"
      >
        <Image name="ai-wand-badge" className="h-10 w-10" />
        <Body size="md" weight="regular" className="mt-4">
          Upload Receipt
        </Body>
        <Body size="sm" weight="regular" className="mt-2 text-white/70">
          Our AI will autofill your details, or you can email your receipt to
          receipts@hypercard.com.
        </Body>
      </label>
    </Card>
  );
};
